import React, { useState, useEffect } from 'react'
import {
  Box, Button, Typography, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Chip
} from '@material-ui/core'
import { previewRender, testCampaign, dryRunCampaign, sendCampaign } from './campaignsApi'

/**
 * Final step: rendered preview (letterhead + body), test-send to internal
 * recipients, then real send. Real send is disabled until a test has been sent.
 */
export default function CampaignReview ({ url, campaign, onStatusChange }) {
  const [renderedHtml, setRenderedHtml] = useState('')
  const [testing, setTesting] = useState(false)
  const [dryRunning, setDryRunning] = useState(false)
  const [sending, setSending] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)
  const [tested, setTested] = useState(!!campaign?.testSentAt)

  useEffect(() => {
    if (!campaign) return
    previewRender(url, { bodyHtml: campaign.bodyHtml, letterheadKey: campaign.letterheadKey })
      .then(res => setRenderedHtml(res.html))
      .catch(e => setError(e.message))
  }, [url, campaign])

  const handleTest = async () => {
    setTesting(true)
    setError(null)
    setMessage(null)
    try {
      const res = await testCampaign(url, campaign._id)
      setTested(true)
      setMessage(`Test sent (${res.stats.sent} email${res.stats.sent === 1 ? '' : 's'}) to internal recipients. Check the debug inbox.`)
      if (onStatusChange) onStatusChange()
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setTesting(false)
    }
  }

  const handleDryRun = async () => {
    setDryRunning(true)
    setError(null)
    setMessage(null)
    try {
      const res = await dryRunCampaign(url, campaign._id)
      setTested(true)
      setMessage(res.message || 'Full dry-run started — every customer email is being sent to the internal testers.')
      if (onStatusChange) onStatusChange()
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setDryRunning(false)
    }
  }

  const handleSend = async () => {
    setConfirmOpen(false)
    setSending(true)
    setError(null)
    setMessage(null)
    try {
      const res = await sendCampaign(url, campaign._id)
      setMessage(res.message || 'Campaign send started.')
      if (onStatusChange) onStatusChange()
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setSending(false)
    }
  }

  if (!campaign) return null

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Review &amp; Send
        {campaign.status && <Chip size="small" label={campaign.status} style={{ marginLeft: 8 }} />}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Subject: <strong>{campaign.subject}</strong>
      </Typography>

      <Box my={2} style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12, background: '#fafafa', maxHeight: 480, overflow: 'auto' }}>
        <div dangerouslySetInnerHTML={{ __html: renderedHtml }} />
      </Box>

      {message && <Typography style={{ color: '#2a9e0d' }} variant="body2" gutterBottom>{message}</Typography>}
      {error && <Typography color="error" variant="body2" gutterBottom>{error}</Typography>}

      <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Button variant="outlined" color="primary" onClick={handleTest} disabled={testing}>
          {testing ? <CircularProgress size={20} /> : 'Send Test (1 preview)'}
        </Button>
        <Button variant="outlined" color="primary" onClick={handleDryRun} disabled={dryRunning}>
          {dryRunning ? <CircularProgress size={20} /> : 'Full Dry-Run → Testers'}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setConfirmOpen(true)}
          disabled={!tested || sending || campaign.status === 'sent' || campaign.status === 'sending'}
        >
          {sending ? <CircularProgress size={20} /> : 'Send For Real'}
        </Button>
        {!tested && <Typography variant="caption" color="textSecondary">Send a test or dry-run first to enable real send.</Typography>}
      </Box>
      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
        <strong>Send Test</strong>: one preview of the letter to the 3 internal testers.&nbsp;
        <strong>Full Dry-Run</strong>: resolves the real audience and sends every customer's email (real names/companies) to the 3 testers — no customers emailed.
      </Typography>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Send campaign to all recipients?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will resolve the audience live and email every matching customer contact.
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleSend} color="secondary" variant="contained">Send now</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
